import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Drawer } from 'antd';
import { InfoCircleOutlined, MailOutlined, PhoneOutlined, HomeOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Employee } from '../services/employeeService';
import { AxiosError } from 'axios';
import { PersonAvatar } from './ui';
import './EmployeeForm.css';

interface ErrorResponse {
  errors: Record<string, string[]>;
}

interface EmployeeFormProps {
  visible: boolean;
  onClose: () => void;
  /** Toasts success/failure itself and rethrows, so field errors (422) can be shown here. */
  onSubmit: (values: Omit<Employee, 'id'>) => Promise<void>;
  initialValues?: Employee;
  title: string;
  /** Shown above the fields, e.g. the password a new employee gets. */
  hint?: string;
}

const EmployeeForm: React.FC<EmployeeFormProps> = ({
  visible,
  onClose,
  onSubmit,
  initialValues,
  title,
  hint
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initialValues);

  // Live preview in the header card
  const name: string | undefined = Form.useWatch('name', form);
  const surname: string | undefined = Form.useWatch('surname', form);
  const email: string | undefined = Form.useWatch('email', form);

  useEffect(() => {
    if (visible) {
      if (initialValues) {
        form.setFieldsValue(initialValues);
      } else {
        form.resetFields();
      }
    }
  }, [visible, initialValues, form]);

  const handleSubmit = async (values: Omit<Employee, 'id'>) => {
    setSaving(true);
    try {
      await onSubmit(values);
      form.resetFields();
      onClose();
    } catch (error) {
      // The page already toasts anything that isn't a field error
      const errors = (error as AxiosError<ErrorResponse>).response?.data?.errors;
      if (errors) {
        form.setFields(Object.keys(errors).map((key) => ({ name: key, errors: errors[key] })));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    form.resetFields();
    onClose();
  };

  const fullName = `${name ?? ''} ${surname ?? ''}`.trim();

  return (
    <Drawer
      title={title}
      placement="right"
      onClose={handleClose}
      open={visible}
      width={500}
      footer={
        <div className="empf-footer">
          <Button onClick={handleClose} disabled={saving}>
            {t('employeeForm.cancel')}
          </Button>
          <Button type="primary" loading={saving} onClick={() => form.submit()}>
            {isEdit ? t('employeeForm.saveChanges') : t('employeeForm.create')}
          </Button>
        </div>
      }
    >
      <div className="empf-identity">
        <PersonAvatar name={name} surname={surname} imageUrl={initialValues?.thumbnailUrl} size={52} />
        <div className="empf-identity-body">
          <div className={`empf-identity-name ${fullName ? '' : 'is-empty'}`}>
            {fullName || t('employeeForm.enterName')}
          </div>
          {email ? <div className="empf-identity-email">{email}</div> : null}
        </div>
      </div>

      {hint ? (
        <div className="empf-hint">
          <InfoCircleOutlined />
          <span>{hint}</span>
        </div>
      ) : null}

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <div className="empf-section-label">{t('employeeForm.sectionPerson')}</div>
        <div className="empf-grid">
          <Form.Item
            name="name"
            label={t('employeeForm.name')}
            rules={[
              { required: true, whitespace: true, message: t('employeeForm.formValidation.nameRequired') },
              { min: 2, message: t('employeeForm.formValidation.nameMinLength') }
            ]}
          >
            <Input
              prefix={<UserOutlined className="empf-icon" />}
              placeholder={t('employeeForm.enterName')}
              maxLength={255}
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item
            name="surname"
            label={t('employeeForm.surname')}
            rules={[
              { required: true, whitespace: true, message: t('employeeForm.formValidation.surnameRequired') },
              { min: 2, message: t('employeeForm.formValidation.surnameMinLength') }
            ]}
          >
            <Input placeholder={t('employeeForm.enterSurname')} maxLength={255} autoComplete="off" />
          </Form.Item>
        </div>

        <div className="empf-section-label">{t('employeeForm.sectionContact')}</div>
        <Form.Item
          name="email"
          label={t('employeeForm.email')}
          extra={t('employeeForm.emailHint')}
          rules={[
            { required: true, message: t('employeeForm.formValidation.emailRequired') },
            { type: 'email', message: t('employeeForm.formValidation.emailInvalid') }
          ]}
        >
          <Input
            prefix={<MailOutlined className="empf-icon" />}
            type="email"
            placeholder={t('employeeForm.enterEmail')}
            maxLength={255}
            autoComplete="off"
          />
        </Form.Item>

        <Form.Item
          name="phone"
          label={t('employeeForm.phone')}
          rules={[
            { pattern: /^[0-9+\-\s()]+$/, message: t('employeeForm.formValidation.phoneInvalid') }
          ]}
        >
          {/* 20 = the backend's max:20 */}
          <Input
            prefix={<PhoneOutlined className="empf-icon" />}
            type="tel"
            inputMode="tel"
            placeholder={t('employeeForm.enterPhone')}
            maxLength={20}
            autoComplete="off"
          />
        </Form.Item>

        <Form.Item name="address" label={t('employeeForm.address')}>
          <Input
            prefix={<HomeOutlined className="empf-icon" />}
            placeholder={t('employeeForm.enterAddress')}
            maxLength={255}
            autoComplete="off"
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default EmployeeForm;
